// ═══════════════════════════════════════════════════════════
// HotWash Store — Observations CRUD with full field support
// ═══════════════════════════════════════════════════════════

import { create } from 'zustand';
import { fetchRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';

const FO = FIELDS.observations;

// ── Types ─────────────────────────────────────────────────

export type ObservationTier = 'observation' | 'improvement_needed' | 'problem_identified' | 'critical_error';
export const TIER_KEYS: ObservationTier[] = ['observation', 'improvement_needed', 'problem_identified', 'critical_error'];

export const TIER_META: Record<ObservationTier, { label: string; color: string; bg: string }> = {
  observation:        { label: 'Sustain/Strength',    color: '#16a34a', bg: '#dcfce7' },
  improvement_needed: { label: 'Area for Improvement', color: '#d97706', bg: '#fef3c7' },
  problem_identified: { label: 'Problem Identified',  color: '#ea580c', bg: '#ffedd5' },
  critical_error:     { label: 'Critical Issue',      color: '#dc2626', bg: '#fee2e2' },
};

export type ObservationCategory = 'communication' | 'logistics' | 'personnel' | 'equipment' | 'procedures' | 'medical' | 'other';
export const CATEGORIES: ObservationCategory[] = ['communication', 'logistics', 'personnel', 'equipment', 'procedures', 'medical', 'other'];

export type ObservationPhase = 'preparation' | 'response' | 'recovery' | 'mitigation';
export const PHASES: ObservationPhase[] = ['preparation', 'response', 'recovery', 'mitigation'];

export interface Observation {
  id: number;
  incidentId: number | string | null;
  title: string;
  tier: string;
  category: string;
  description: string;
  recommendation: string;
  suggestedRemedy: string;
  phase: string;
  isAnonymous: boolean;
  status: string;
  submittedBy: string;
  submittedByEmail: string;
  datecreated?: string;
}

// ── Mappers ───────────────────────────────────────────────

function mapObs(r: Record<string, unknown>): Observation {
  const recommendation = (r[FO.recommendation] as string) || '';
  return {
    id: r.record_id as number,
    incidentId: (r[FO.incidentId] as number) || null,
    title: (r[FO.title] as string) || '',
    tier: (r[FO.tier] as string) || 'observation',
    category: (r[FO.category] as string) || 'other',
    description: (r[FO.description] as string) || '',
    recommendation,
    suggestedRemedy: recommendation,   // alias used by types/index.ts pages
    phase: 'response' as any,          // default since field not in DB schema
    isAnonymous: false,
    status: (r[FO.status] as string) || 'open',
    submittedBy: (r[FO.submittedBy] as string) || '',
    submittedByEmail: (r[FO.submittedByEmail] as string) || '',
    assignedPersonId: FO.assignedPersonId ? (r[FO.assignedPersonId] as string) || '' : '',
    datecreated: (r.created_at as string) || '',
  };
}

function obsToPayload(data: Record<string, unknown>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.title !== undefined) p[FO.title] = data.title;
  if (data.tier !== undefined) p[FO.tier] = data.tier;
  if (data.category !== undefined) p[FO.category] = data.category;
  if (data.description !== undefined) p[FO.description] = data.description;
  if (data.recommendation !== undefined) p[FO.recommendation] = data.recommendation;
  if (data.status !== undefined) p[FO.status] = data.status;
  if (data.submittedBy !== undefined) p[FO.submittedBy] = data.submittedBy;
  if (data.submittedByEmail !== undefined) p[FO.submittedByEmail] = data.submittedByEmail;
  if (data.incidentId !== undefined) p[FO.incidentId] = data.incidentId;
  if (FO.assignedPersonId && data.assignedPersonId !== undefined) p[FO.assignedPersonId] = String(data.assignedPersonId);
  return p;
}

// ── Store ─────────────────────────────────────────────────

interface HotwashState {
  observations: Observation[];
  loading: boolean;
  isLoading: boolean;
  fetchObservations: () => Promise<void>;
  createObservation: (data: Record<string, unknown>) => Promise<number | null>;
  updateObservation: (id: number, data: Record<string, unknown>) => Promise<void>;
  deleteObservation: (id: number) => Promise<void>;
  // V1 compat aliases
  loadObservations: () => Promise<void>;
  submitObservation: (data: any) => Promise<number | null>;
}

export const useHotwashStore = create<HotwashState>((set, get) => ({
  observations: [],
  loading: false,

  fetchObservations: async () => {
    set({ loading: true });
    try {
      const r = await fetchRecords(TABLES.observations, { limit: 500, sort: { field: 'created_at', direction: 'desc' } });
      set({ observations: r.records.map(mapObs as any) });
    } catch (e) { console.error('[HW]', e); }
    set({ loading: false });
  },

  createObservation: async (data) => {
    try {
      const r = await createRecord<any>(TABLES.observations, obsToPayload(data));
      await get().fetchObservations();
      return r.record_id ?? r.id ?? null;
    } catch (e) { console.error('[HW]', e); return null; }
  },

  updateObservation: async (id, data) => {
    try {
      await updateRecord(TABLES.observations, id, obsToPayload(data));
      await get().fetchObservations();
    } catch (e) { console.error('[HW]', e); }
  },

  deleteObservation: async (id) => {
    try {
      await deleteRecord(TABLES.observations, id);
      set(s => ({ observations: s.observations.filter(o => o.id !== id) }));
    } catch (e) { console.error('[HW]', e); }
  },

  // V1 compat aliases
  isLoading: false,
  loadObservations: async () => { await (get() as any).fetchObservations(); },
  submitObservation: async (data: any) => { return await (get() as any).createObservation(data); },
}));
